import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Wrench, ArrowLeft, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { api, formatApiErrorDetail } from "../lib/api";

export default function AdminLogin() {
    const nav = useNavigate();
    const [email, setEmail] = useState("admin@fixhub.com");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (localStorage.getItem("fixhub_token")) nav("/admin", { replace: true });
    }, [nav]);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const { data } = await api.post("/auth/login", { email, password });
            localStorage.setItem("fixhub_token", data.access_token);
            localStorage.setItem("fixhub_user", JSON.stringify(data.user));
            toast.success("Welcome back, " + (data.user.name || "Admin"));
            nav("/admin", { replace: true });
        } catch (err) {
            toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Login failed");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center px-6 grid-pattern" data-testid="admin-login-page">
            <div className="absolute top-6 left-6">
                <Link
                    to="/"
                    data-testid="back-to-site"
                    className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white transition-colors"
                >
                    <ArrowLeft className="h-4 w-4" /> Back to site
                </Link>
            </div>
            <div className="w-full max-w-md rounded-2xl border border-white/10 bg-[#0A0A0F]/90 backdrop-blur-xl p-10">
                <div className="flex items-center gap-3 mb-8">
                    <span className="inline-flex h-10 w-10 items-center justify-center rounded-sm bg-[#00E5FF] text-black">
                        <Wrench className="h-5 w-5" />
                    </span>
                    <div>
                        <div className="font-display text-xl font-bold">Mr Mobile Admin</div>
                        <div className="text-xs text-zinc-500">Staff console</div>
                    </div>
                </div>
                <h1 className="font-display text-2xl font-semibold tracking-tight">Sign in</h1>
                <p className="text-sm text-zinc-400 mt-1">Manage incoming quote requests.</p>
                <form onSubmit={submit} className="mt-8 space-y-5" data-testid="admin-login-form">
                    <div className="space-y-2">
                        <Label htmlFor="email" className="text-zinc-300">Email</Label>
                        <Input
                            id="email"
                            type="email"
                            required
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            data-testid="login-email"
                            className="bg-white/5 border-white/10 text-white focus:border-[#00E5FF] focus-visible:ring-[#00E5FF]"
                        />
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="password" className="text-zinc-300">Password</Label>
                        <Input
                            id="password"
                            type="password"
                            required
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            data-testid="login-password"
                            className="bg-white/5 border-white/10 text-white focus:border-[#00E5FF] focus-visible:ring-[#00E5FF]"
                        />
                    </div>
                    <button
                        type="submit"
                        disabled={loading}
                        data-testid="login-submit"
                        className="w-full bg-[#00E5FF] text-black font-bold py-3 rounded-sm hover:bg-[#4DEDFF] transition-colors disabled:opacity-60"
                    >
                        {loading ? "Signing in…" : "Sign in"}
                    </button>
                </form>
                <div className="mt-6 flex items-center gap-2 text-xs text-zinc-500">
                    <ShieldCheck className="h-4 w-4 text-[#00E5FF]" />
                    Session encrypted • Admin-only access
                </div>
            </div>
        </div>
    );
}
